// tabs/character-description.ts — client-side port of the PC sheet's Character
// Description tab (options/game/character_sheet_display/character_description/
// _character_description_inc.php).
//
// Modernized: the heavy Swiper/lightGallery carousel + duplicate upload buttons are
// replaced by a lightweight main-image + thumbnail-strip picture gallery
// rendered from caster.player_characters.{pictures,videos}; everything else (exp marks,
// description, background, characteristics, traits/ideals/bonds/flaws) reads the blob
// and parse_text()s the rich-text fields. Owner-only Edit affordances open the existing
// sidelog edit forms; media management still routes to the unified Media Manager.

import type { CachedCaster } from '../types';
import { num, pcField } from '../read-helpers';
import { parseText } from '../../shared/parse-text';
import { sizeIdOf } from '../../shared/array-types';
import { catalogNameById } from './ability-row';
import { pcPictureRow } from '../../shared/pc-picture-pair';

type CharacterDescriptionWindow = Window & {
    openItemInfo?: (itemId: string) => unknown;
    forceOpenSideLog?: () => unknown;
    forceOpenSideInfoPanel?: () => unknown;
    queryData?: (url: string, targetId: string, payload: Record<string, string>) => unknown;
    renderSizeInfoPanel?: (casterId: string) => unknown;
    renderReachInfoPanel?: (casterId: string) => unknown;
    renderAttacksInfoPanel?: (casterId: string) => unknown;
};

// catalog ids used verbatim from the PHP (info-panel links / fallbacks).
const EXP_MARKS_INFO_ID = 'A0419AE8-7882-1A04-6094-DD75CAD79164';
const SIZE_TINY_FALLBACK = ''; // SizeType::getId('tiny') — left blank client-side; size is virtually always set.
const REACH_INFO_FALLBACK = '36D59FF4-6778-96ED-8664-F5D63638C77B';

function descAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-pc-desc-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function descInfoAttrs(itemId: string): string {
    return descAttrs('open-info', { itemId });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-pc-desc-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftPcDescAction ?? '';
    const w = window as CharacterDescriptionWindow;
    if (action === 'open-info') {
        const itemId = el.dataset.ftItemId ?? '';
        if (itemId !== '') { event.preventDefault(); w.openItemInfo?.(itemId); }
    } else if (action === 'edit-form') {
        event.preventDefault();
        w.forceOpenSideLog?.();
        document.getElementById('pills_sidelog_form_tab')?.click();
        const form = el.dataset.ftForm ?? '';
        const casterId = el.dataset.ftCasterId ?? '';
        if (form !== '') {
            w.queryData?.(`${BASE_URL}options/tools/player_characters/forms/${form}`, 'div_refresh_sidelog_form', { caster_id: casterId });
        }
    } else if (action === 'gallery-select') {
        event.preventDefault();
        ftPcGallerySelect(el);
    } else if (action === 'size-info') {
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderSizeInfoPanel?.(el.dataset.ftCasterId ?? '');
    } else if (action === 'reach-info') {
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderReachInfoPanel?.(el.dataset.ftCasterId ?? '');
    } else if (action === 'attacks-info') {
        // Extra Attack counter (core-attacks.ts) → the per-turn attack-budget breakdown panel
        // (Base attack + every extra_attack source with provenance). Same panel the creature-summary
        // "Total Attacks" cell opens; reads computed.creature.attacks.{per_turn,extra_attack}.
        event.preventDefault();
        w.forceOpenSideInfoPanel?.();
        w.renderAttacksInfoPanel?.(el.dataset.ftCasterId ?? '');
    }
});

function canEdit(caster: CachedCaster): boolean {
    return typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
}

/** Owner-only Edit button that opens a sidelog player-edit form. */
function editButton(form: string, casterId: string, btnCls = 'btn-dark'): string {
    return `<button type="button" class="ft-pc-desc__edit btn btn-sm ${btnCls} border" aria-label="Edit" ${descAttrs('edit-form', { form, casterId })}>${iconMap('edit')}</button>`;
}

// ── gallery ──────────────────────────────────────────────────────────────────
interface MediaItem { src: string; preview: string; primary: boolean }

function collectMedia(caster: CachedCaster): MediaItem[] {
    const pc = caster.player_characters;
    const out: MediaItem[] = [];
    const pics = pc?.pictures?.player_character_picture;
    if (Array.isArray(pics)) {
        const uploads = new Map<string, typeof pics>();
        pics.forEach((row, index) => {
            const raw = row?.picture_number;
            const number = Array.isArray(raw) ? raw[0] : raw;
            // Rows with no upload identity are their own upload — never grouped together.
            const key = number == null || number === '' ? `missing:${index}` : String(number);
            const group = uploads.get(key) ?? [];
            group.push(row);
            uploads.set(key, group);
        });
        for (const group of uploads.values()) {
            const full = pcPictureRow(group, 'full');
            const avatar = pcPictureRow(group, 'avatar');
            const src = String(pictureField0(full, 'pic_url') || '');
            const preview = String(pictureField0(avatar, 'pic_url') || src);
            if (src) out.push({ src, preview, primary: group.some(row => num(row?.primary_picture) === 1) });
        }
    }
    // Primary picture first, otherwise keep source order.
    out.sort((a, b) => Number(b.primary) - Number(a.primary));
    return out;
}

function mediaMainHtml(item: MediaItem | undefined): string {
    if (!item) return `<div class="ft-pc-desc__media ft-pc-desc__media--empty" data-ft-gallery-main>No pictures yet.</div>`;
    return `<div class="ft-pc-desc__media" data-ft-gallery-main><img src="${escapeHtml(item.src)}" alt="" loading="lazy"></div>`;
}

function thumbHtml(item: MediaItem, active: boolean): string {
    return `<button type="button" class="ft-pc-desc__thumb${active ? ' is-active' : ''}" data-media-type="image" data-media-src="${escapeHtml(item.src)}" ${descAttrs('gallery-select')}><img src="${escapeHtml(item.preview)}" alt="" loading="lazy" style="object-fit:contain"></button>`;
}

function buildGalleryHtml(caster: CachedCaster, casterId: string): string {
    const media = collectMedia(caster);
    const isOwner = canEdit(caster);
    const galleryUrl = `${BASE_URL}tools/media/media_manager.php?type=player_character&id=${escapeHtml(casterId)}`;

    const main = mediaMainHtml(media[0]);
    const thumbs = media.length > 1
        ? `<div class="ft-pc-desc__thumbs">${media.map((m, i) => thumbHtml(m, i === 0)).join('')}</div>`
        : '';

    let actions = '';
    if (isOwner) {
        const manage = `<a href="${galleryUrl}" class="btn btn-primary btn-sm flex-grow-1 flex-md-grow-0">${iconMap('modify_picture_gallery')} Manage Gallery</a>`;
        const upload = media.length === 0
            ? `<a href="${BASE_URL}tools/player_characters/upload/upload_player_character_picture.php?id=${escapeHtml(casterId)}" class="btn btn-primary btn-sm flex-grow-1 flex-md-grow-0">${iconMap('upload_player_character_picture')} Upload First Picture</a>`
            : '';
        actions = `<div class="d-flex flex-wrap gap-2 mt-2 character-description-gallery-actions">${manage}${upload}</div>`;
    }

    return `<div class="ft-pc-desc__gallery" data-ft-gallery>${main}${thumbs}${actions}</div>`;
}

// ── field blocks ─────────────────────────────────────────────────────────────
/** A bordered field card: title + optional edit + parse_text(value) | empty state. */
function fieldCard(opts: { title: string; valueHtml: string; edit?: string; iconKey?: string }): string {
    const icon = opts.iconKey ? `<span class="ft-pc-desc__chip-icon">${iconMap(opts.iconKey)}</span>` : '';
    return `<section class="ft-pc-desc__field">
        <header class="ft-pc-desc__head">${icon}<h6 class="ft-pc-desc__title">${escapeHtml(opts.title)}</h6>${opts.edit ?? ''}</header>
        <div class="ft-pc-desc__value">${opts.valueHtml}</div>
    </section>`;
}

function richValueOrEmpty(caster: CachedCaster, key: string): string {
    const raw = pcField(caster, key);
    const text = raw === undefined || raw === null ? '' : String(raw);
    return text !== '' ? parseText(text) : `<span class="ft-pc-desc__empty">None</span>`;
}

function buildExpMarks(caster: CachedCaster, casterId: string): string {
    const edit = canEdit(caster) ? editButton('form_edit_player_experience_marks_inc.php', casterId, 'btn-primary') : '';
    const marks = pcField(caster, 'experience_marks');
    const head = `<header class="ft-pc-desc__head">
        <h6 class="ft-pc-desc__title" role="button" ${descInfoAttrs(EXP_MARKS_INFO_ID)}>Experience Marks</h6>${edit}</header>`;
    return `<section class="ft-pc-desc__field">${head}<div class="ft-pc-desc__value">${escapeHtml(String(marks ?? 0))}</div></section>`;
}

/** Resolve a UUID → item_name from a caster blob's OWN embedded `.catalog` (when the seed carries
 *  one). The cross-root catalogNameById walk MISSES the in-encounter case where the caster is a
 *  member node with NO own catalog and the identity lives on the (separate) encounter root — but it
 *  also can't see a standalone seed's own `.catalog`. This direct read covers the latter. */
function nameFromCasterCatalog(caster: CachedCaster, id: string): string {
    const cat = caster.catalog;
    if (!cat) return '';
    const e = cat[id] ?? cat[id.toUpperCase()] ?? cat[id.toLowerCase()];
    if (!e) return '';
    const src = e.items ?? e;
    const v = Array.isArray(src.item_name) ? src.item_name[0] : src.item_name;
    return typeof v === 'string' ? v : '';
}

function buildBackground(caster: CachedCaster): string {
    // player_characters.background is a PROVIDER SLOT. Its shape varies by seed:
    //   • standalone item.php seed → a full {items:{…}} nest (item_name readable via ftItemsCol);
    //   • encounter/catalogized seed → a BARE UUID string, identity hoisted into a `.catalog`.
    // The old code only did ftItemsCol(bg,'item_name'), which returns '' for the bare-UUID shape →
    // "None". Mirror the proven pcSlotIdName() helper (tabs/summary.ts) used by the header badges,
    // then resolve the name through every reachable channel so it works regardless of which cache
    // root carries the catalog (caster's own → cross-root casterCache/encounterCache → global).
    const bg = pcField(caster, 'background');
    let bgId = '';
    let bgName = '';
    if (typeof bg === 'string') {
        bgId = bg;
    } else if (bg && typeof bg === 'object') {
        bgId = typeof ftItemsCol === 'function' ? String(ftItemsCol(bg, 'item_id') ?? '') : '';
        bgName = typeof ftItemsCol === 'function' ? String(ftItemsCol(bg, 'item_name') ?? '') : '';
    }
    if (bgName === '' && bgId !== '') {
        bgName = nameFromCasterCatalog(caster, bgId)
            || catalogNameById(bgId)
            || (typeof ftItemNameById === 'function' ? String(ftItemNameById(bgId) ?? '') : '');
    }
    const value = bgId !== '' && bgName !== ''
        ? `<h5 class="m-0" role="button" ${descInfoAttrs(bgId)}><span class="badge bg-success">${escapeHtml(bgName)}</span></h5>`
        : `<span class="ft-pc-desc__empty">None</span>`;
    return fieldCard({ title: 'Background', valueHtml: value });
}

// ── characteristics (chips) ──────────────────────────────────────────────────
function sizeId(caster: CachedCaster): string {
    // PHP layer_value('size') bridges to computed.creature.size.breakdown.final_size_id
    // (a PC has no item_layer_types); sizeIdOf is the shared TS twin of that bridge.
    return sizeIdOf(caster) ?? '';
}

function plainChip(label: string, value: unknown, suffix = ''): string {
    if (value === undefined || value === null || value === '') return `<span class="ft-pc-desc__chip"><b>${escapeHtml(label)}:</b> <span class="ft-pc-desc__muted">—</span></span>`;
    return `<span class="ft-pc-desc__chip"><b>${escapeHtml(label)}:</b> ${escapeHtml(String(value))}${suffix}</span>`;
}

function buildCharacteristics(caster: CachedCaster, casterId: string): string {
    const sid = sizeId(caster);
    const sizeClickId = sid !== '' ? sid : SIZE_TINY_FALLBACK;
    // Size breakdown renders client-side from computed.creature.size.breakdown (+ the size term
    // interned into the caster .catalog) — no PHP round-trip. See info-panel/size-info.ts.
    const sizeChip = sid !== ''
        ? `<span class="ft-pc-desc__chip" role="button" data-ft-info-item-id="${escapeHtml(sizeClickId)}" ${descAttrs('size-info', { casterId })}><b>${iconMap('size')}:</b> ${iconMap(sid)}</span>`
        : plainChip('Size', '');

    const reach = num(ftLayerValue(caster, 'reach'));
    const reachChip = `<span class="ft-pc-desc__chip" role="button" data-ft-info-item-id="${REACH_INFO_FALLBACK}" ${descAttrs('reach-info', { casterId })}><b>${iconMap('reach')}:</b> ${reach}</span>`;

    const grossWeight = caster.computed?.creature?.weight?.gross_weight ?? caster.gross_weight ?? null;

    const chips = [
        sizeChip,
        reachChip,
        plainChip('Gender', pcField(caster, 'gender')),
        plainChip('Height', pcField(caster, 'height')),
        plainChip('Weight', pcField(caster, 'weight'), ' lbs.'),
        plainChip('Gross Weight', grossWeight, ' lbs.'),
        plainChip('Age', pcField(caster, 'age')),
        plainChip('Hair', pcField(caster, 'hair')),
        plainChip('Eyes', pcField(caster, 'eyes')),
        plainChip('Skin', pcField(caster, 'skin')),
        plainChip('Faith', pcField(caster, 'faith')),
    ].join('');

    return `<section class="ft-pc-desc__field">
        <header class="ft-pc-desc__head"><h6 class="ft-pc-desc__title">Characteristics</h6></header>
        <div class="ft-pc-desc__chips">${chips}</div>
    </section>`;
}

// ── traits / ideals / bonds / flaws ──────────────────────────────────────────
interface MoralField { key: string; title: string; icon: string; infoId: string; form: string; accent: string }
const MORAL_FIELDS: ReadonlyArray<MoralField> = [
    { key: 'player_traits', title: 'Traits', icon: 'traits', infoId: '7A667D09-FCEE-42CB-80DC-7260959714C3', form: 'form_edit_player_traits_inc.php', accent: '#3b82f6' },
    { key: 'player_ideals', title: 'Ideals', icon: 'ideals', infoId: '5F074D3C-5D70-0E0D-0AF6-D30AA05333D8', form: 'form_edit_player_ideals_inc.php', accent: '#94a3b8' },
    { key: 'player_bonds', title: 'Bonds', icon: 'bonds', infoId: '2B9A63C3-4A7D-A618-178E-058D16453BA9', form: 'form_edit_player_bonds_inc.php', accent: '#22c55e' },
    { key: 'player_flaws', title: 'Flaws', icon: 'flaws', infoId: '55B5A6C6-93FD-A7BD-45F9-754AA921F1DE', form: 'form_edit_player_flaws_inc.php', accent: '#f59e0b' },
];

function buildMoralField(caster: CachedCaster, f: MoralField, casterId: string): string {
    const edit = canEdit(caster) ? editButton(f.form, casterId, 'btn-dark') : '';
    const head = `<header class="ft-pc-desc__head" style="--ftpc-field-accent:${f.accent};">
        <span class="ft-pc-desc__chip-icon" role="button" ${descInfoAttrs(f.infoId)}>${iconMap(f.icon)}</span>
        <h6 class="ft-pc-desc__title">${escapeHtml(f.title)}</h6>${edit}</header>`;
    return `<section class="ft-pc-desc__field ft-pc-desc__field--accent" style="--ftpc-field-accent:${f.accent};">${head}<div class="ft-pc-desc__value">${richValueOrEmpty(caster, f.key)}</div></section>`;
}

// ── top-level builder ────────────────────────────────────────────────────────
export function buildCharacterDescriptionHtml(caster: CachedCaster, casterId: string): string {
    const descEdit = canEdit(caster) ? editButton('form_edit_player_description_inc.php', casterId, 'btn-primary') : '';

    const body = `<div class="ft-pc-desc__body">
        ${buildExpMarks(caster, casterId)}
        ${fieldCard({ title: 'Description', valueHtml: richValueOrEmpty(caster, 'player_description'), edit: descEdit })}
        ${buildBackground(caster)}
        ${buildCharacteristics(caster, casterId)}
        ${MORAL_FIELDS.map((f) => buildMoralField(caster, f, casterId)).join('')}
    </div>`;

    return `<div class="ft-pc-desc">
        ${buildGalleryHtml(caster, casterId)}
        ${body}
    </div>`;
}

// ── gallery interaction (inline onclick target; survives pane re-render) ──────
function ftPcGallerySelect(btn: HTMLElement): void {
    const gallery = btn.closest('[data-ft-gallery]');
    if (!gallery) return;
    const mainHost = gallery.querySelector('[data-ft-gallery-main]') as HTMLElement | null;
    if (!mainHost) return;
    const type = btn.getAttribute('data-media-type');
    const src = btn.getAttribute('data-media-src') || '';
    mainHost.classList.remove('ft-pc-desc__media--empty');
    mainHost.innerHTML = type === 'video'
        ? `<video controls playsinline src="${escapeHtml(src)}"></video>`
        : `<img src="${escapeHtml(src)}" alt="" loading="lazy">`;
    gallery.querySelectorAll('.ft-pc-desc__thumb').forEach((t) => t.classList.remove('is-active'));
    btn.classList.add('is-active');
}
Object.assign(window, { ftPcGallerySelect });
